'use client'

import type { ChangeEvent } from 'react'
import clsx from 'clsx'

import Error from '/react4xp/components/prototype/Form/Error'
import ErrorCircle from '/react4xp/components/prototype/icons/ErrorCircle'
import { useFormField } from '/react4xp/utils/prototype/useFormField'

import type { TextareaProps } from './types'

export default function Textarea({
	id,
	name,
	label,
	floatingLabel = false,
	placeholder,
	required = false,
	error: externalError,
	invalid: externalInvalid,
	className,
	onChange: externalOnChange,
	requiredMessage,
	...props
}: TextareaProps) {
	const { finalError, finalInvalid, updateFormValue, getRegisterProps } = useFormField({
		name,
		externalError,
		externalInvalid,
		required,
		requiredMessage,
	})
	const errorId = `${id}-error`

	const registerProps = getRegisterProps()

	function handleChange(e: ChangeEvent<HTMLTextAreaElement>) {
		externalOnChange?.(e)
		updateFormValue(e.target.value)
	}

	return (
		<div className={clsx('form-field', className)}>
			{label && !floatingLabel && (
				<label htmlFor={id} className="form-label">
					{label}
					{required && (
						<span aria-hidden="true" className="form-label-required">
							*
						</span>
					)}
				</label>
			)}
			<div className="group form-text-field">
				<textarea
					{...registerProps}
					id={id}
					name={name}
					placeholder={floatingLabel ? placeholder || ' ' : placeholder}
					required={required}
					aria-invalid={finalInvalid || undefined}
					aria-describedby={finalError ? errorId : undefined}
					className={clsx(
						'form-text-input form-textarea-input',
						floatingLabel && 'peer form-text-input--floating',
					)}
					onChange={handleChange}
					{...props}
				/>
				{label && floatingLabel && (
					<label htmlFor={id} className="form-floating-label form-floating-label--top">
						{label}
						{required && (
							<span aria-hidden="true" className="ml-0.5">
								*
							</span>
						)}
					</label>
				)}
				{finalError && finalInvalid && (
					<ErrorCircle className="form-textarea-error-icon" aria-hidden="true" />
				)}
			</div>
			{finalError && finalInvalid && <Error label={finalError} id={errorId} showIcon={false} />}
		</div>
	)
}
